import { S25Util } from "../../util/s25-util";
import { AccessLevels, type Fls } from "../../pojo/Fls";
import type { EventMicroI, S25Event } from "../s25-event/EventMicroI";
import type { S25WsNode } from "../../pojo/S25WsNode";
import type { S25WsSpace } from "../../services/space.service";
import type { S25WsResource } from "../../services/resource.service";
import type { S25Profile } from "../s25-event/ProfileI";
import type { ObjectType, S25Reservation } from "../s25-event/ReservationI";
import {
    AvailWSResponse,
    InlineEditCheckAvailabilityResult,
    ResourceSpaceAvailService,
} from "../../services/resource.space.avail.service";
import WSSpacesAvail = AvailWSResponse.WSSpacesAvail;
import WSResourcesAvail = AvailWSResponse.WSResourcesAvail;
import WSSpaceAvail = AvailWSResponse.WSSpaceAvail;
import WSResourceAvail = AvailWSResponse.WSResourceAvail;
import DateElement = AvailWSResponse.DateElement;
import AvailConflict = AvailWSResponse.Conflict;
import { jSith } from "../../util/jquery-replacement";
import { Item } from "../../pojo/Item";
import { Event } from "../../pojo/Event";
import Rsrv = Event.Reservation.States;
import type { AvailCheck } from "../../pojo/AvailabilityCheck";
import { ObjectSearchAvailabilityUtil } from "../../util/object.search.availability.util";
import type { ObjectSearchAvail } from "./object.search.avail";
import type { Proto } from "../../pojo/Proto";
import type { Nullable } from "../../pojo/Util";
type ISODurationString = Proto.ISODurationString;
type Reservation = AvailCheck.Reservation;
type SpaceReservation = AvailCheck.SpaceReservation;
type CheckedObj = AvailCheck.CheckedObj;
type ConflictDefaults = AvailCheck.ConflictDefaults;
type ResourceReservation = AvailCheck.ResourceReservation;
type CheckedItem = AvailCheck.CheckedItem;

interface AvailabilityCheckResponse {
    resourceConflicts: ConflictResponseItem[];
    spaceConflicts: ConflictResponseItem[];
}

interface ConflictResponseItem {
    itemTypeId: number;
    itemId: string;
    resource_id?: string;
    candidateDate: string;
    conflictHash?: string;
    apNoRequest?: boolean;
    apViolationDate?: string;
    apReason?: string;
    conflictRelation?: "direct" | "also";
    conflictType?: string;
    conflictName?: string;
    conflictStart?: string;
    conflictEnd?: string;
    conflictShare?: string;
    conflictOverride?: boolean;
    occUUID?: number;
    isReal?: boolean;
    candidateId?: string | number;
    conflictId?: string | number;
    hasConflicts?: boolean;
    hasRealConflicts?: boolean;
    reservation_start_dt?: string;
}

export type CheckedSpaceReservation = SpaceReservation & CheckedItem & { itemName?: string; hasConflicts?: boolean };
export type CheckedResourceReservation = ResourceReservation &
    CheckedItem & { itemName?: string; hasConflicts?: boolean };

type AvailCheckConflict = ConflictDefaults & {
    apNoRequest?: boolean;
    apViolationDate?: string | Date;
    apReason?: string;
};

export class S25ObjectSearchUtil {
    public static async getAvailData(
        objectType: ObjectType,
        profileId: number,
        eventId: number,
        accumItems: SpaceReservation[] | ResourceReservation[],
        event: EventMicroI,
        selectedItems: number[],
    ): Promise<(CheckedSpaceReservation | CheckedResourceReservation)[]> {
        const reservations = await S25ObjectSearchUtil.normalizeReservationData(
            objectType,
            accumItems,
            profileId,
            event,
        );
        const specificOccs = reservations.filter((item: Reservation) => selectedItems.includes(item.rsrv ?? 0));
        const occurrencesByContext = S25Util.array.groupBy(
            specificOccs,
            (occurrence) => `${occurrence.eventId ?? eventId}-${occurrence.profileId ?? profileId}`,
        );
        const occurrenceGroups = Object.values(occurrencesByContext).filter(
            (group): group is Reservation[] => !!group?.length,
        );
        const contextResults = await Promise.all(
            occurrenceGroups.map((contextOccurrences) => {
                const [firstOccurrence] = contextOccurrences;
                return ResourceSpaceAvailService.inlineEditCheckAvailability(
                    firstOccurrence.eventId ?? eventId,
                    firstOccurrence.profileId ?? profileId,
                    contextOccurrences,
                );
            }),
        );
        const combinedResult = contextResults.reduce<InlineEditCheckAvailabilityResult>(
            (combined, result) => ({
                resourceConflicts: [...combined.resourceConflicts, ...result.resourceConflicts],
                spaceConflicts: [...combined.spaceConflicts, ...result.spaceConflicts],
                allResAvailDateMap: { ...combined.allResAvailDateMap, ...result.allResAvailDateMap },
            }),
            { resourceConflicts: [], spaceConflicts: [], allResAvailDateMap: {} },
        );
        return S25ObjectSearchUtil.checkedObj(accumItems, combinedResult, specificOccs);
    }

    public static async normalizeReservationData(
        objectType: ObjectType,
        accumItems: SpaceReservation[] | ResourceReservation[],
        profileId: number,
        event: EventMicroI,
    ) {
        const findProfile = event.profile.find((p: S25Profile) => {
            return p?.itemId === profileId;
        });
        const reservations: Reservation[] = [];

        if (findProfile?.reservations) {
            let count = 0;
            let space_reservation: SpaceReservation[] = [];
            let resource_reservation: ResourceReservation[] = [];
            if (objectType === "location") {
                space_reservation = (accumItems as SpaceReservation[]).map((item: SpaceReservation) => {
                    return {
                        space_id: item.space_id,
                        share: item.share,
                        always_shared: item.always_shared, // Assuming always_shared is "F" as there is no equivalent property in the input data
                        conflicts: [],
                    } as SpaceReservation;
                });
            }

            if (objectType === "resource") {
                resource_reservation = (accumItems as ResourceReservation[]).map((item: ResourceReservation) => {
                    return {
                        resource_id: item.resource_id,
                        quantity: 1,
                        conflicts: [],
                        apViolationUUIDs: new Set<number>(),
                    } as ResourceReservation;
                });
            }

            findProfile?.reservations.forEach((reservation: S25Reservation) => {
                if (reservation.state !== Rsrv.Cancelled) {
                    const rsrv: Reservation = {
                        occUUID: S25Util.isDefined(reservation.occUUID) ? reservation.occUUID : count++,
                        reservation_start_dt: (reservation?.setupStart ||
                            reservation?.preEventStart ||
                            reservation.eventStart)!,
                        reservation_end_dt: (reservation?.takeDownEnd ||
                            reservation?.postEventEnd ||
                            reservation.eventEnd)!,
                        space_reservation: space_reservation,
                        resource_reservation: resource_reservation,
                        eventId: reservation.eventId,
                        profileId: reservation.profileId,
                        segmentName: reservation.segmentName,
                        isBoundSegment: reservation.isBoundSegment,
                        rsrv: reservation.itemId,
                        state: { itemId: 1, itemName: "Active" },
                    };
                    reservations.push(rsrv);
                }
            });
        }
        return reservations;
    }

    public static checkedObj(
        accumItems: (SpaceReservation | ResourceReservation)[],
        reservations: InlineEditCheckAvailabilityResult,
        specificOccs: Reservation[],
    ): (CheckedSpaceReservation | CheckedResourceReservation)[] {
        const numDatesToCheck: number = specificOccs.length;
        // processResourceAvailability reads stock levels off the occurrence list, and the availability
        // check only attaches them to the per-context arrays it was handed (ANG-2515)
        const occurrencesWithStock = Object.assign([...specificOccs], {
            allResAvailDateMap: reservations.allResAvailDateMap,
        });
        const itemTypeId = reservations.resourceConflicts.length
            ? reservations.resourceConflicts[0].itemTypeId
            : reservations?.spaceConflicts[0]?.itemTypeId;
        const checkedItems =
            itemTypeId === Item.Ids.Location ? reservations.spaceConflicts : reservations.resourceConflicts;

        jSith.forEach(accumItems, function (_, itm) {
            const item = itm as CheckedSpaceReservation | CheckedResourceReservation;
            item.checkedObj = {
                conflicts: [],
                hasConflicts: false,
                hasRealConflicts: false,
                totalDatesChecked: numDatesToCheck,
                availableDates: numDatesToCheck,
                conflictingDates: 0,
                availableDatesWithOverrides: numDatesToCheck,
            };
            item.apRestriction = undefined;

            jSith.forEach(checkedItems, function (_, rsrv) {
                const conflict = rsrv as AvailCheckConflict;
                const candidateId = S25ObjectSearchUtil.conflictCandidateId(conflict);
                if (item.itemId !== candidateId) return;
                const candidateOccurrence = specificOccs.find((occurrence) => occurrence.occUUID === conflict.occUUID);
                const displayConflict: AvailCheckConflict = {
                    ...conflict,
                    candidateName: candidateOccurrence?.segmentName,
                    candidateIsBound: candidateOccurrence?.isBoundSegment,
                };

                if (itemTypeId === Item.Ids.Location) {
                    if (
                        conflict.apNoRequest &&
                        S25Util.date.equal(
                            S25Util.date.dropTZString(conflict.apViolationDate),
                            S25Util.date.dropTZString(conflict.candidateDate),
                        )
                    ) {
                        item.apRestriction = {
                            startDt: new Date(conflict.apViolationDate!),
                            apNoRequest: true,
                            reason: conflict.apReason!,
                        };
                    }

                    S25ObjectSearchUtil.appendConflictIfNew(item, displayConflict, itemTypeId);
                }

                if (itemTypeId === Item.Ids.Resource) {
                    if (
                        conflict.apNoRequest &&
                        S25Util.date.equal(
                            S25Util.date.dropTZString(conflict.apViolationDate),
                            S25Util.date.dropTZString(conflict.date ?? conflict.candidateDate),
                        )
                    ) {
                        item.apRestriction = {
                            startDt: new Date(conflict.apViolationDate!),
                            apNoRequest: true,
                            reason: conflict.apReason!,
                        };
                    }

                    S25ObjectSearchUtil.appendConflictIfNew(item, displayConflict, itemTypeId);
                }
            });

            if (item.checkedObj) {
                ObjectSearchAvailabilityUtil.syncCheckedObjAvailabilityCounts(item.checkedObj);
                item.hasConflicts = item.checkedObj.hasConflicts;
            }

            if (item.itemTypeId === Item.Ids.Resource) {
                item.itemId = (item as ResourceReservation).resource_id;
                ObjectSearchAvailabilityUtil.processResourceAvailability(item, occurrencesWithStock);
            }

            item.checkedObj?.conflicts.sort(S25Util.shallowSortDates("candidateDate", "conflictStart"));
        });

        return accumItems as (CheckedSpaceReservation | CheckedResourceReservation)[];
    }

    private static conflictCandidateId(conflict: { candidateId?: string | number; itemId?: string | number }): number {
        const raw = conflict.candidateId ?? conflict.itemId ?? 0;
        return typeof raw === "string" ? parseInt(raw) : raw;
    }

    private static isSameConflictDate(existing: ConflictDefaults, conflict: ConflictDefaults): boolean {
        return (
            existing.conflictHash === conflict.conflictHash &&
            existing.occUUID === conflict.occUUID &&
            S25Util.date.equal(existing.candidateDate, conflict.candidateDate)
        );
    }

    private static appendConflictIfNew(
        item: CheckedSpaceReservation | CheckedResourceReservation,
        conflict: ConflictDefaults,
        itemTypeId: Item.Ids,
    ): void {
        if (!item.checkedObj) return;
        if (item.checkedObj.conflicts.some((existing) => S25ObjectSearchUtil.isSameConflictDate(existing, conflict))) {
            return;
        }

        item.hasConflicts = true;
        item.checkedObj.conflicts.push({
            ...conflict,
            itemId: item.itemId,
            itemName: item.itemName || conflict.itemName || "",
            itemTypeId: itemTypeId as Item.Id,
        });
    }

    /**
     * Checks if a list of conflicts contains only request conflicts without any reservation/block conflicts.
     * req_rsrv : a space that has been requested
     * req_block : the space being reserved has a blocked by relationship with a space that has been requested for the same time
     * @param conflicts - Array of conflict objects to check
     * @returns true if there are request conflicts and no reservation/block conflicts
     */
    public static hasOnlyRequestConflicts(conflicts: Nullable<ConflictDefaults[]>): boolean {
        const hasRequestConflicts = !!conflicts?.some((c: ConflictDefaults) =>
            S25ObjectSearchUtil.isRequestConflictType(c.conflictType),
        );
        const hasRsrvBlockConflicts = !!conflicts?.some((c: ConflictDefaults) =>
            S25ObjectSearchUtil.isRealConflictType(c.conflictType),
        );
        return hasRequestConflicts && !hasRsrvBlockConflicts;
    }

    public static isRequestConflictType(conflictType: AvailCheck.conflictTypes): boolean {
        return ["req_rsrv", "req_block"].includes(conflictType);
    }

    public static isRealConflictType(conflictType: AvailCheck.conflictTypes): boolean {
        return ["rsrv", "block"].includes(conflictType);
    }

    /**
     * Checks if an object has only request conflicts (convenience wrapper)
     * @param obj - The location or resource item to check
     * @returns true if there are request conflicts and no reservation/block conflicts
     */
    public static isRequestConflict(obj: AvailLocationItems | AvailResourceItems): boolean {
        const conflicts = obj.checkedObj?.conflicts ?? [];
        return S25ObjectSearchUtil.hasOnlyRequestConflicts(conflicts);
    }

    /**
     * Checks if an object has confirmed reservation conflicts (rsrv or block types).
     * These are conflicts with actual confirmed reservations, not just requests.
     * @param obj - The location or resource item to check
     * @returns true if there are confirmed reservation conflicts
     */
    public static hasConfirmedReservationConflicts(obj: AvailLocationItems | AvailResourceItems): boolean {
        const conflicts = obj.checkedObj?.conflicts ?? [];
        return conflicts.some((c: ConflictDefaults) => ["rsrv", "block"].includes(c.conflictType));
    }

    /**
     * Gets all confirmed reservation conflicts (rsrv or block types) from an object.
     * @param obj - The location or resource item to check
     * @returns Array of conflicts that are confirmed reservations
     */
    public static getConfirmedReservationConflicts(obj: AvailLocationItems | AvailResourceItems): ConflictDefaults[] {
        const conflicts = obj.checkedObj?.conflicts ?? [];
        return conflicts.filter((c: ConflictDefaults) => ["rsrv", "block"].includes(c.conflictType));
    }

    public static maxLayoutTimes(
        itemsList: readonly (ObjectSearchAvail.AddedItem | { layouts?: Layout[] })[],
    ): LayoutTimes {
        return itemsList
            .flatMap((item) => ("layouts" in item ? (item.layouts ?? []) : []))
            .filter((l) => l.default_layout === "T")
            .reduce<LayoutTimes>(
                (acc, l) => ({
                    setup: Math.max(acc.setup, S25Util.ISODurationToMinutes(l.layout_setup_tm ?? "") || 0),
                    takedown: Math.max(acc.takedown, S25Util.ISODurationToMinutes(l.layout_tdown_tm ?? "") || 0),
                }),
                { setup: 0, takedown: 0 },
            );
    }

    /**
     * Re-applies setup/takedown times onto layouts after a WS refresh (matched by layout_id).
     * Used when the Layout Setup/Takedown modal reduced times and setObjectDetails would
     * otherwise restore the location's default layout times from the server.
     */
    public static applyLayoutTimeOverrides(
        layouts: Layout[] | undefined,
        overrides: readonly Pick<Layout, "layout_id" | "layout_setup_tm" | "layout_tdown_tm">[] | undefined,
    ): void {
        if (!layouts?.length || !overrides?.length) return;
        const overrideById = new Map(
            overrides
                .filter((override) => override.layout_id != null)
                .map((override) => [override.layout_id!, override]),
        );
        for (const layout of layouts) {
            const override = layout.layout_id != null ? overrideById.get(layout.layout_id) : undefined;
            if (!override) continue;
            if (override.layout_setup_tm != null) layout.layout_setup_tm = override.layout_setup_tm;
            if (override.layout_tdown_tm != null) layout.layout_tdown_tm = override.layout_tdown_tm;
        }
    }

    public static getProfileTimes(event: S25Event, profileId: number): LayoutTimes {
        const profile = event?.profile?.find((p) => p.itemId === profileId);
        const added = profile?.occurrenceDefn?.addedTime ?? {};
        return {
            setup: S25Util.ISODurationToMinutes(added.setup ?? "") ?? 0,
            takedown: S25Util.ISODurationToMinutes(added.takedown ?? "") ?? 0,
        };
    }

    public static canOverrideConflict(conflictType: AvailCheck.conflictTypes, fls: Fls): boolean {
        if (S25ObjectSearchUtil.isRequestConflictType(conflictType)) return true;
        switch (conflictType) {
            case "hours":
                return fls?.SPACE_HOURS === AccessLevels.Full;
            case "blackout":
                return fls?.SPACE_BLACK === AccessLevels.Full;
            case "block":
                return fls?.SPACE_BLOCKED === AccessLevels.Full;
            default:
                return false;
        }
    }

    /**
     * ANG-7691 + ANG-7706: build conflict rows for the layout times modal from segment-wide avail checks.
     */
    public static buildLayoutExtensionConflicts(
        spaceAvailability: { spaces?: WSSpacesAvail } | undefined,
        resourceAvailability: { resources?: WSResourcesAvail } | undefined,
        fls: Fls,
    ): ConflictDefaults[] {
        const conflicts: ConflictDefaults[] = [];
        const nonOverridableSpaces =
            spaceAvailability?.spaces?.space?.filter(
                (space: WSSpaceAvail) =>
                    space.has_conflicts === "T" &&
                    !space.dates?.every(
                        (date: DateElement) =>
                            !date.conflict?.length ||
                            date.conflict.every((conflict: AvailConflict) =>
                                S25ObjectSearchUtil.canOverrideConflict(conflict.conflict_type, fls),
                            ),
                    ),
            ) ?? [];
        if (nonOverridableSpaces.length) {
            conflicts.push(
                ...ResourceSpaceAvailService.toDisplayConflicts(
                    { spaces: { space: nonOverridableSpaces } },
                    "space",
                    fls,
                ),
            );
        }

        const conflictResources =
            resourceAvailability?.resources?.resource?.filter(
                (resource: WSResourceAvail) => resource.has_conflicts === "T",
            ) ?? [];
        if (conflictResources.length) {
            conflicts.push(
                ...ResourceSpaceAvailService.toDisplayConflicts(
                    { resources: { resource: conflictResources } },
                    "resource",
                    fls,
                ),
            );
        }
        return conflicts;
    }

    public static adjustDates(dates: any[], setupDiff: number, takeDownDiff: number) {
        return dates.map((d) => ({
            ...d,
            startDt: setupDiff > 0 ? S25Util.date.addMinutes(d.startDt, -setupDiff) : d.startDt,
            endDt: takeDownDiff > 0 ? S25Util.date.addMinutes(d.endDt, takeDownDiff) : d.endDt,
        }));
    }

    public static shouldExcludeExpressLocations(
        excludeExpress: boolean,
        isAssociatedLocationsSearch: boolean,
    ): boolean {
        return excludeExpress && !isAssociatedLocationsSearch;
    }

    public static isAssociatedLocationsSearch(
        savedSearchChosen?: { isAssociatedLocations?: boolean; val?: string | number },
        profilePrefSearch?: { isAssociatedLocations?: boolean; val?: string | number },
        activeQuery?: string,
    ): boolean {
        if (savedSearchChosen?.isAssociatedLocations) return true;
        if (
            profilePrefSearch?.isAssociatedLocations &&
            activeQuery &&
            profilePrefSearch.val != null &&
            activeQuery === String(profilePrefSearch.val)
        ) {
            return true;
        }
        return false;
    }

    public static filterLocationSearchResults<T extends { assign_perm?: string; direct_schedule?: unknown }>(
        items: T[],
        options: { excludeExpress: boolean; isAssociatedLocationsSearch: boolean },
    ): T[] {
        const excludeExpress = S25ObjectSearchUtil.shouldExcludeExpressLocations(
            options.excludeExpress,
            options.isAssociatedLocationsSearch,
        );
        S25Util.array.inplaceFilter(items, (item) => {
            //ANG-4015 assign_perm can be C (2 - assign),N (1 - request), X (0 - not requestable) and replaces the OLS.schedule permission.
            if (item.assign_perm === "X") return false;
            if (excludeExpress && item.direct_schedule) return false;
            return true;
        });
        return items;
    }
}

export interface AvailLocationItems extends S25WsSpace, Layout, CheckedObj {
    itemTypeId?: Item.Ids;
    conflicts: ConflictDefaults[];
    share?: "F" | "T";
    instructions?: string;
    layoutId?: number | null;
    action?: Function;
    layouts?: Layout[];
}
export interface AvailResourceItems extends S25WsResource, CheckedObj {
    allResAvailDateMap?: Map<string, number>;
    itemTypeId?: Item.Ids;
    apRestriction?: {
        startDt: Date;
        apNoRequest: boolean;
        reason: string;
        occUUIDs?: Set<number>;
    };
    availability?: number | string;
    maxAvailable?: number;
    minAvailable?: number;
    quantity?: number | string;
    conflicts: ConflictDefaults[];
    instructions?: string;
}

export interface Layout {
    layout_diagram_type?: string;
    layout_id?: number;
    layout_name?: string;
    layout_setup_tm?: string;
    layout_photo_name?: string;
    layout_description_id?: number;
    layout_photo_glyph?: string;
    layout_instruction?: string;
    layout_diagram_id?: number;
    layout_diagram_glyph?: string;
    default_layout?: "T" | "F";
    layout_photo_id?: number;
    layout_photo_type?: string;
    layout_tdown_tm?: string;
    layout_capacity?: number;
    layout_diagram_name?: string;
    layout_defn_state?: number;
    layout_description?: string;
    layout_instruction_id?: number;
    status?: S25WsNode["status"]; //'mod' | 'est' | 'del' | 'new' ;
    layout_ratio?: number;
    itemId?: number;
    itemName?: string;
}

export namespace Layout {
    /**
     * A selected layout does not always come from the item's layout list: event hydration builds one
     * from the reservation, and the list is replaced when the WS returns. Compare by id instead.
     */
    export function isSame(left?: Layout | null, right?: Layout | null): boolean {
        return left?.layout_id === right?.layout_id;
    }

    export function label(layout: Layout): string {
        const name = layout.layout_name ?? "";
        return layout.layout_capacity == null ? name : `${name} (${layout.layout_capacity})`;
    }
}

export interface UrlParams {
    inputParam?: string;
    query?: string;
    criteria?: string;
    include?: string;
    page_size?: string; //&page_size=25
    page?: string; // "&page=1"
    paginate?: string; // "&paginate="
}

export interface LayoutTimes {
    setup: number;
    takedown: number;
}

export interface LayoutTimesDuration {
    itemName?: string;
    setup?: ISODurationString;
    takedown?: ISODurationString;
    /** ANG-7691: conflicts found when extending setup/takedown across the segment */
    conflicts?: AvailCheck.ConflictDefaults[];
}
